import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MATTER_STATUSES,
  formatDateHuman,
  type MatterRow,
} from "@themislex/shared";
import { useApi } from "../../api/hooks";
import {
  EmptyState,
  ErrorNote,
  MatterStatusBadge,
  PageHeader,
  Select,
  Spinner,
} from "../../components/ui";

const STATUS_LABELS: Record<string, string> = {
  intake: "Intake",
  active: "Active",
  on_hold: "On hold",
  closed: "Closed",
  archived: "Archived",
};

export default function Matters() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const status = params.get("status") ?? "";
  const sort = params.get("sort") === "hearing" ? "hearing" : "updated";
  const clientId = params.get("clientId") ?? "";

  const search = new URLSearchParams();
  if (query.trim()) search.set("q", query.trim());
  if (status) search.set("status", status);
  if (clientId) search.set("clientId", clientId);
  if (sort === "hearing") search.set("sort", "hearing");

  const { data, error, loading } = useApi<{ matters: MatterRow[] }>(
    `/api/matters?${search.toString()}`,
  );

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Matters"
        subtitle="All matters across the practice."
        actions={
          <Link to="/desk/matters/new" className="btn btn-primary no-underline">
            New matter
          </Link>
        }
      />

      <div className="card mb-6 grid gap-4 p-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className="label" htmlFor="matter-search">
            Search
          </label>
          <input
            id="matter-search"
            className="input"
            type="search"
            placeholder="Title, case number or client…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Status"
            value={status}
            onChange={(e) => update("status", e.target.value)}
          >
            <option value="">All</option>
            {MATTER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
          <Select
            label="Order by"
            value={sort}
            onChange={(e) => update("sort", e.target.value)}
          >
            <option value="updated">Last updated</option>
            <option value="hearing">Hearing date</option>
          </Select>
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && (
        <div className="flex justify-center py-16 text-muted">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {data &&
        (data.matters.length === 0 ? (
          <EmptyState
            title="No matters found."
            hint="Adjust the filters, or open a new matter."
            action={
              <Link to="/desk/matters/new" className="btn btn-primary no-underline">
                New matter
              </Link>
            }
          />
        ) : (
          <div className="card overflow-x-auto p-2">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">Client</th>
                  <th scope="col">Case type</th>
                  <th scope="col">Case number</th>
                  <th scope="col">Court / forum</th>
                  <th scope="col">Next hearing</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.matters.map((matter) => (
                  <tr key={matter.id}>
                    <td className="font-medium">
                      <Link
                        to={`/desk/matters/${matter.id}`}
                        className="text-accent-text hover:underline"
                      >
                        {matter.title}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/desk/clients/${matter.clientId}`}
                        className="text-accent-text hover:underline"
                      >
                        {matter.clientName}
                      </Link>
                    </td>
                    <td>{matter.caseType}</td>
                    <td>{matter.caseNumber ?? "—"}</td>
                    <td>{matter.court ?? "—"}</td>
                    <td className="whitespace-nowrap">
                      {formatDateHuman(matter.nextHearingDate)}
                    </td>
                    <td>
                      <MatterStatusBadge status={matter.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </>
  );
}
